import React from "react";
import { TiArrowSortedUp, TiArrowSortedDown } from "react-icons/ti";
import { FiInfo } from "react-icons/fi";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import "./FinanceDashboard.css";

const METRIC_EXPLANATIONS = {
  revenue: "Total revenue generated from orders and services during the selected period.",
  sales: "Total sales revenue generated from orders and services during the selected period.",
  gross_profit: "Sales revenue minus direct cost of goods sold (COGS).",
  net_profit: "Total net earnings remaining after deducting all operational costs and expenses.",
  ebitda: "Earnings before interest, tax, depreciation, and amortization.",
  food_cost: "Percentage of total sales spent on food ingredients and inventory.",
  labor_cost: "Percentage of total sales spent on employee wages and staffing.",
  labour_cost: "Percentage of total sales spent on employee wages and staffing.",
  operating_expense: "Total costs incurred for business operations and overheads.",
  opex: "Total costs incurred for business operations and overheads.",
  cash_flow: "Net cash inflows minus cash outflows during the selected timeframe.",
  payable: "Outstanding unpaid invoices owed to suppliers and vendors.",
  receivable: "Unpaid invoices and balances due from corporate or credit clients.",
  gross_margin: "Percentage of revenue retained after deducting direct cost of sales.",
  net_margin: "Percentage of revenue retained as net profit after all expenses.",
};

const getMetricExplanation = (card) => {
  if (card.description) return card.description;
  if (card.info) return card.info;
  if (card.tooltip) return card.tooltip;

  const key = (card.key || "").toLowerCase();
  const title = (card.title || "").toLowerCase();

  for (const [k, exp] of Object.entries(METRIC_EXPLANATIONS)) {
    if (key.includes(k) || title.includes(k.replace("_", " "))) {
      return exp;
    }
  }

  return `Financial metric showing total ${card.title || card.key || "performance"} for the selected period.`;
};

// SVG Sparkline component for smooth area curves
const FinanceSparkline = ({ color = "#2563eb", points = [15, 25, 18, 30, 24, 38, 32, 42] }) => {
  const width = 200;
  const height = 38;

  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = max - min || 1;

  const coords = points.map((val, idx) => {
    const x = (idx / (points.length - 1)) * width;
    const y = height - 4 - ((val - min) / range) * (height - 10);
    return { x, y };
  });

  let pathD = `M ${coords[0].x} ${coords[0].y}`;
  for (let i = 0; i < coords.length - 1; i++) {
    const curr = coords[i];
    const next = coords[i + 1];
    const cpX = (curr.x + next.x) / 2;
    pathD += ` C ${cpX} ${curr.y}, ${cpX} ${next.y}, ${next.x} ${next.y}`;
  }

  const fillD = `${pathD} L ${width} ${height} L 0 ${height} Z`;
  const gradId = `spark-grad-${Math.random().toString(36).substring(2, 9)}`;

  return (
    <svg
      width="100%"
      height="38"
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      style={{ display: "block", overflow: "visible" }}
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0.0" />
        </linearGradient>
      </defs>
      <path d={fillD} fill={`url(#${gradId})`} />
      <path
        d={pathD}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

// Preset curve generators based on key/trend
const getSparklinePoints = (card) => {
  if (Array.isArray(card.sparkline_data) && card.sparkline_data.length >= 2) {
    return card.sparkline_data;
  }
  const key = (card.key || "").toLowerCase();
  const trend = (card.trend || "up").toLowerCase();
  const isUp = trend === "up";

  if (key.includes("revenue") || key.includes("sales")) {
    return isUp
      ? [15, 28, 12, 14, 26, 32, 22, 38]
      : [35, 28, 30, 20, 22, 15, 18, 10];
  }
  if (key.includes("profit") || key.includes("ebitda") || key.includes("margin")) {
    return isUp
      ? [18, 32, 22, 28, 36, 25, 38, 34]
      : [34, 25, 30, 18, 22, 15, 12, 8];
  }
  if (key.includes("cash") || key.includes("bank")) {
    return isUp
      ? [12, 16, 20, 22, 25, 28, 26, 32]
      : [32, 28, 25, 22, 20, 16, 14, 10];
  }
  if (key.includes("inventory") || key.includes("payable") || key.includes("expense")) {
    return isUp
      ? [10, 18, 15, 25, 28, 20, 26, 30]
      : [30, 25, 22, 18, 15, 12, 10, 8];
  }

  return isUp
    ? [12, 18, 16, 24, 22, 32, 28, 36]
    : [36, 28, 30, 22, 20, 16, 14, 10];
};

const getSparklineColor = (card) => {
  if (card.sparkline_color) return card.sparkline_color;
  const key = (card.key || "").toLowerCase();

  if (key.includes("revenue") || key.includes("sales") || key.includes("cash")) {
    return "#2563eb"; // Blue
  }
  if (key.includes("profit") || key.includes("gross") || key.includes("ebitda")) {
    return "#16a34a"; // Green
  }
  if (key.includes("payable") || key.includes("expense") || key.includes("inventory")) {
    return "#f59e0b"; // Amber/Orange
  }
  return "#2563eb";
};

const formatCardValue = (val, key = "", title = "") => {
  if (val === null || val === undefined) return "0";
  if (
    typeof val === "string" &&
    (val.includes("Rs") || val.includes("%") || val.includes("$") || val.includes("Cr") || val.includes("M"))
  ) {
    return val;
  }
  const num = Number(val);
  if (isNaN(num)) return String(val);

  const isPercent =
    title.includes("%") ||
    key.includes("margin") ||
    key.includes("cost_pct") ||
    key.includes("percent");

  if (isPercent) {
    return `${num.toFixed(1)}%`;
  }

  if (num >= 10000000) {
    return `Rs ${(num / 10000000).toFixed(2)} Cr`;
  }

  return `Rs ${num.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
};

const FinanceStatsShow = ({ cardsData = [], isLoading = false }) => {
  if (isLoading) {
    return (
      <div className="finance-stats-grid">
        {[1, 2, 3, 4, 5].map((item) => (
          <div key={`skel-${item}`} className="finance-stat-col">
            <div className="finance-stat-card">
              <div>
                <div className="finance-card-header">
                  <div className="finance-skeleton-box" style={{ width: "60%", height: "14px" }} />
                  <div className="finance-skeleton-box" style={{ width: "16px", height: "16px", borderRadius: "50%" }} />
                </div>
                <div className="finance-skeleton-box my-2" style={{ width: "75%", height: "24px" }} />
                <div className="finance-skeleton-box" style={{ width: "45%", height: "12px" }} />
              </div>
              <div className="mt-3">
                <div className="finance-skeleton-box" style={{ width: "100%", height: "36px", borderRadius: "6px" }} />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  const listToRender = Array.isArray(cardsData) ? cardsData : [];

  if (listToRender.length === 0) {
    return null;
  }

  return (
    <div className="finance-stats-grid">
      {listToRender.map((card, idx) => {
        const key = card.key || `card-${idx}`;
        const title = card.title || card.key || "Metric";
        const currentValue = card.current_value;
        const formattedVal = formatCardValue(currentValue, key, title);
        const changePercent = card.change_percent ?? 0;
        const trend = card.trend || "up";
        const isUp = trend === "up" || changePercent > 0;
        const isDown = trend === "down" || changePercent < 0;
        const isFlat = !isUp && !isDown;

        // Check if progress bar should be displayed
        const hasTarget = card.target !== undefined && card.target !== null;
        const isPercentageMetric =
          title.includes("%") || key.includes("margin") || key.includes("cost") || hasTarget;

        const targetVal = hasTarget ? Number(card.target) : 60.0;
        const actualVal = isPercentageMetric ? Number(currentValue) || 50.0 : 0;
        const barColor = key.includes("food") || key.includes("labour") ? "#f59e0b" : "#16a34a";

        return (
          <div key={key} className="finance-stat-col">
            <div className="finance-stat-card">
              <div>
                {/* Header */}
                <div className="finance-card-header">
                  <span className="finance-card-title">
                    {title}
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id={`stat-tooltip-${key}`}>
                          {getMetricExplanation(card)}
                        </Tooltip>
                      }
                    >
                      <span className="finance-info-trigger" style={{ cursor: "pointer" }}>
                        <FiInfo className="finance-card-info-icon" />
                      </span>
                    </OverlayTrigger>
                  </span>
                  {card.badge && (
                    <span className="finance-card-badge">{card.badge}</span>
                  )}
                </div>

                {/* Main Value */}
                <div className="finance-card-value">{formattedVal}</div>

                {/* Trend & Subtext */}
                <div className="finance-card-trend-row">
                  {isUp && (
                    <span className="finance-card-trend-up d-flex align-items-center">
                      <TiArrowSortedUp /> {Math.abs(changePercent).toFixed(1)}%
                    </span>
                  )}
                  {isDown && (
                    <span className="finance-card-trend-down d-flex align-items-center">
                      <TiArrowSortedDown /> {Math.abs(changePercent).toFixed(1)}%
                    </span>
                  )}
                  {isFlat && (
                    <span className="finance-card-trend-flat d-flex align-items-center">
                      0.0%
                    </span>
                  )}
                  <span className="finance-card-trend-label">vs prev</span>
                </div>

                {card.sub_text && (
                  <div className="finance-card-subtext">{card.sub_text}</div>
                )}
              </div>

              {/* Bottom Visual Element: Progress Bar vs Sparkline */}
              {isPercentageMetric ? (
                <div className="finance-card-progress-container">
                  <div className="finance-card-progress-bar-bg">
                    <div
                      className="finance-card-progress-fill"
                      style={{
                        width: `${Math.min(100, Math.max(0, actualVal))}%`,
                        backgroundColor: barColor,
                      }}
                    />
                    <div
                      className="finance-card-target-line"
                      style={{ left: `${Math.min(100, Math.max(0, targetVal))}%` }}
                      title={`target ${targetVal}%`}
                    />
                  </div>
                  <div className="finance-card-progress-labels">
                    <span>actual {actualVal.toFixed(1)}%</span>
                    <span>target {targetVal.toFixed(1)}%</span>
                  </div>
                </div>
              ) : (
                <div className="finance-card-chart">
                  <FinanceSparkline
                    color={getSparklineColor(card)}
                    points={getSparklinePoints(card)}
                  />
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default FinanceStatsShow;